/**
 * Console Log Monkey Patch
 *
 * This module intercepts console.log messages to provide special handling for legacy code
 * and third-party libraries that don't use our SfLog logging library.
 *
 * Key functionality:
 * - Catches console messages that bypass SfLog (legacy code, 3rd party libraries)
 * - Specifically monitors for "JQMigrate" warnings from jQuery migrate plugin
 * - Logs JQMigrate issues to Sentry for tracking deprecated jQuery usage
 *
 * Context:
 * - SfLog is our main logging adapter that internally uses console.log() in dev mode
 *   and routes messages to Sentry/other services based on environment/configuration
 * - This monkey patch handles cases where SfLog isn't used at all
 * - Particularly useful for identifying jQuery code that needs updating/attention
 */

import $ from 'jquery'
import _ from 'lodash'

type ConsoleMethods = 'debug' | 'info' | 'log' | 'warn' | 'error' | 'trace'
type ConsoleLogMap = {
  [key in ConsoleMethods]: (...args: unknown[]) => void
}

// To hide the warning: localStorage.setItem("hideConsoleMonkeyPatchWarning", "true")
window.addEventListener(
  'DOMContentLoaded',
  () => {
    if (
      $('body').data('environment') === 'development' &&
      localStorage.getItem('hideConsoleMonkeyPatchWarning') !== 'true'
    ) {
      console.warn('- console.(debug|info|log|warn|error) monkey-patched')
    }
  },
  { once: true }
)

$(document).on('turbolinks:load', () => {
  const methods: ConsoleMethods[] = [
    'debug',
    'info',
    'log',
    'warn',
    'error',
    'trace',
  ]

  // We have to take the direct references to the actual function like this
  // instead of doing just: originalConsole = console
  const originalConsole = _.reduce(
    methods,
    (memo, methodName) => {
      memo[methodName] = _.bind(console[methodName], console)
      return memo
    },
    {} as ConsoleLogMap
  )

  const patchedConsole = _.reduce(
    methods,
    (memo, methodName) => {
      memo[methodName] = function (...args): void {
        // Logging to Sentry when console.log JQMigrate message
        if (args[0] && _.isString(args[0])) {
          if (args[0].includes('JQMigrate')) {
            if (window.Sentry) {
              window.Sentry.captureMessage(`JQMigrate issue: ${args[0]}`, {
                level: 'warning',
              })
            }
          }
        }

        originalConsole[methodName].apply(this, args)
      }

      return memo
    },
    {} as ConsoleLogMap
  )

  const methodKeys = Object.keys(patchedConsole) as ConsoleMethods[]

  // Apply monkey-patch to global.console
  methodKeys.forEach((methodName: ConsoleMethods): void => {
    window.console[methodName] = patchedConsole[methodName]
  })
})
